import { CompanyManagement } from "@/admin/CompanyManagement";
import { formatDate, label } from "@/admin/model";
import { ActivityDiagnostics } from "@/admin/ActivityDiagnostics";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { ExternalLink } from "lucide-react";
import { useAdminStore } from "@/stores/adminStore";
import { useSystemStore } from "@/stores/systemStore";
import { loadAdminCompanyDetail, updateAdminCommercial } from "@/data/firestore/adminRepository";
import { AnalyticsMetrics, RecentActivity, SelectField, Status } from "@/admin/components";
import type { Company } from "@/admin/model";
import type { CommercialState } from "@/domain/types";
import { PageHeader } from "@/components/PageHeader";
import { EmptyState, ErrorState, LoadingState } from "@/components/DataState";
import { useDiscoveryHost } from "@/components/discovery-host-context";
import { Button } from "@/components/ui/button";
import { toAppError, type AppError } from "@/data/errors";

export const Route = createFileRoute("/admin/companies/$customerId")({ component: CompanyDetail });
function CompanyDetail() {
  const { customerId } = Route.useParams();
  return <CompanyDetailContent key={customerId} customerId={customerId} />;
}
function CompanyDetailContent({ customerId }: { customerId: string }) {
  const { openDiscovery } = useDiscoveryHost();
  const state = useAdminStore();
  const [detail, setDetail] = useState<Awaited<ReturnType<typeof loadAdminCompanyDetail>> | null>(
    null,
  );
  const [error, setError] = useState<AppError | null>(null);
  const [attempt, setAttempt] = useState(0);
  const company = state.companies.find((c) => c.id === customerId);
  useEffect(() => {
    let active = true;
    setError(null);
    void loadAdminCompanyDetail(customerId)
      .then((value) => {
        if (active) setDetail(value);
      })
      .catch((error) => {
        if (active) setError(toAppError(error));
      });
    return () => {
      active = false;
    };
  }, [customerId, attempt]);
  if (state.status === "error") return <ErrorState error={state.error} />;
  if (state.status === "idle" || state.status === "loading")
    return <LoadingState label="Loading company…" />;
  if (!company) return <EmptyState message="This company is not available." />;
  const profile = detail?.profile ?? state.profiles[customerId];
  return (
    <>
      <Link to="/admin/companies" className="text-sm font-semibold text-primary hover:underline">
        ← Companies
      </Link>
      <div className="[&>header]:flex [&>header]:flex-wrap [&>header>div:first-child]:w-full sm:[&>header>div:first-child]:w-auto">
        <PageHeader
          title={profile?.name || company.domain || company.id}
          subtitle={company.domain || company.website}
          action={
            <div className="flex flex-wrap gap-2">
              <Button asChild variant="outline" size="sm">
                <Link to="/app/$customerId" params={{ customerId }} search={{ adminView: true }}>
                  <ExternalLink className="size-4" />
                  Open Dashboard
                </Link>
              </Button>
              {profile?.publicSlug && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  aria-haspopup="dialog"
                  onClick={() =>
                    openDiscovery({
                      publicCustomerSlug: profile.publicSlug,
                      mode: "demo",
                      adminView: true,
                    })
                  }
                >
                  <ExternalLink className="size-4" />
                  Open Discovery
                </Button>
              )}
            </div>
          }
        />
      </div>
      <div className="grid items-start gap-5 lg:grid-cols-2">
        <section className="surface-elevated p-5">
          <h2 className="text-lg font-extrabold">Company</h2>
          <dl className="mt-4 grid grid-cols-[auto_minmax(0,1fr)] gap-x-5 gap-y-3 text-sm">
            {[
              ["Name", profile?.name || "Not available"],
              ["Domain", company.domain || "Not recorded"],
              ["Customer ID", company.id],
              ["Public slug", profile?.publicSlug || "Not available"],
              ["Primary language", profile?.language || "Not recorded"],
              ["Root status", label(company.status)],
              ["Onboarding", label(company.onboardingStatus)],
              [
                "Qotta fit",
                company.qottaFit === null ? "Unknown" : company.qottaFit ? "Yes" : "No",
              ],
              ["Fit reason", company.qottaFitReason || "Not recorded"],
              ["Created", formatDate(company.createdAt)],
              ["Last updated", formatDate(company.updatedAt)],
            ].map(([name, value]) => (
              <div key={name} className="contents">
                <dt className="text-muted-foreground">{name}</dt>
                <dd className="break-words">{value}</dd>
              </div>
            ))}
          </dl>
        </section>
        <CommercialEditor company={company} />
      </div>
      {detail?.profileError && (
        <p role="alert" className="text-sm text-warning">
          The company profile could not refresh. Previously loaded profile information is shown when
          available.
        </p>
      )}
      {detail && (
        <Button variant="outline" size="sm" onClick={() => setAttempt((n) => n + 1)}>
          Refresh company details
        </Button>
      )}
      {error ? (
        <div className="space-y-3">
          <ErrorState error={error} />
          <Button variant="outline" onClick={() => setAttempt((n) => n + 1)}>
            Retry company details
          </Button>
        </div>
      ) : !detail ? (
        <LoadingState label="Loading members and activity…" />
      ) : (
        <>
          <CompanyManagement customerId={customerId} refreshKey={attempt} onChange={() => setAttempt((n) => n + 1)} />
          <section className="space-y-3">
            <h2 className="text-lg font-extrabold">Company activity</h2>
            <p className="text-xs text-muted-foreground">
              All stored records, including persisted previews.
            </p>
            {detail.analytics ? (
              <>
                <p className="text-xs text-muted-foreground">
                  Updated {formatDate(detail.analytics.updatedAt)}
                </p>
                <AnalyticsMetrics value={detail.analytics} />
                <RecentActivity analytics={detail.analytics} />
              </>
            ) : (
              <ErrorState error={detail.analyticsError} />
            )}
          </section>
        </>
      )}
      <ActivityDiagnostics
        key={customerId}
        customerId={customerId}
        company={profile?.name || company.domain || company.id}
      />
    </>
  );
}
function CommercialEditor({ company }: { company: Company }) {
  const authorized = useSystemStore((s) => s.adminAccessResolved && s.isSystemAdmin);
  const [type, setType] = useState(company.commercial?.type ?? "");
  const [access, setAccess] = useState(company.commercial?.accessStatus ?? "");
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const dirty =
    type !== (company.commercial?.type ?? "") ||
    access !== (company.commercial?.accessStatus ?? "");
  if (!authorized) return null;
  async function save() {
    setSaving(true);
    setMessage("");
    try {
      await updateAdminCommercial(company.id, { type, accessStatus: access } as CommercialState);
      setMessage("Commercial settings saved.");
    } catch (error) {
      setMessage(
        error instanceof Error ? error.message : "Commercial settings could not be saved.",
      );
    } finally {
      setSaving(false);
    }
  }
  return (
    <section className="surface-elevated space-y-4 p-5">
      <h2 className="text-lg font-extrabold">Commercial</h2>
      <div className="flex flex-wrap gap-2">
        <Status value={company.commercial?.type} />
        <Status value={company.commercial?.accessStatus} />
      </div>
      <SelectField
        label="Commercial type"
        value={type}
        onChange={(value) => setType(value as typeof type)}
        disabled={saving}
        options={[
          ["", "Choose type"],
          ["standard", "Standard"],
          ["design_partner", "Design partner"],
        ]}
      />
      <SelectField
        label="Commercial access"
        value={access}
        onChange={(value) => setAccess(value as typeof access)}
        disabled={saving}
        options={[
          ["", "Choose access status"],
          ["inactive", "Inactive"],
          ["active", "Active"],
          ["suspended", "Suspended"],
        ]}
      />
      <p className="text-xs leading-5 text-muted-foreground">
        Changes affect commercial eligibility. Company membership is unchanged.
      </p>
      <Button disabled={!type || !access || !dirty || saving} onClick={() => void save()}>
        {saving ? "Saving…" : "Save commercial settings"}
      </Button>
      {message && (
        <p role="status" className="text-sm">
          {message}
        </p>
      )}
    </section>
  );
}
